import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { expandedLayerIdsAtom, layersAtom, layerTreeAtom, selectedLayerIdAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { LayerType } from '../data/v1'
import { useLayerTreeActions } from '../use-layer-tree-actions'
import { DropSlot, type HotTarget } from './layer-list-dnd'
import { NodeView } from './layer-list-node'

export function LayerList() {
	const store = useCanvasStore()
	const layerTree = useAtomValue(layerTreeAtom, { store })
	const layers = useAtomValue(layersAtom, { store })
	const setSelectedId = useSetAtom(selectedLayerIdAtom, { store })
	const { moveNode } = useLayerTreeActions()

	const [expanded, setExpanded] = useAtom(expandedLayerIdsAtom, { store })

	const toggleExpand = useCallback((id: string) => {
		setExpanded(prev => {
			const next = new Set(prev)
			if (next.has(id)) { next.delete(id) }
			else { next.add(id) }
			return next
		})
	}, [setExpanded])

	const [draggingId, setDraggingId] = useState<string | null>(null)
	const [draggingType, setDraggingType] = useState<LayerType | null>(null)
	const [hotTarget, setHotTarget] = useState<HotTarget>(null)

	const hotTargetRef = useRef<HotTarget>(null)
	hotTargetRef.current = hotTarget

	useEffect(() => {
		return monitorForElements({
			onDragStart: ({ source }) => {
				setDraggingId(source.data.id as string)
				setDraggingType(source.data.type as LayerType)
			},
			onDrag: ({ location }) => {
				const target = location.current.dropTargets[0]
				if (!target || target.data._kind !== 'slot') {
					setHotTarget(null)
					return
				}
				setHotTarget({
					slotKey: target.data.slotKey as string,
					parentId: target.data.parentId as string | null,
					idx: target.data.idx as number,
				})
			},
			onDrop: ({ source, location }) => {
				const dragId = source.data.id as string
				const target = location.current.dropTargets[0]
				if (target && target.data._kind === 'slot') {
					const targetParentId = target.data.parentId as string | null
					const targetIdx = target.data.idx as number
					moveNode(dragId, targetParentId, targetIdx)
				}
				setSelectedId(dragId)
				setDraggingId(null)
				setDraggingType(null)
				setHotTarget(null)
			},
		})
	}, [moveNode])

	const sharedProps = {
		draggingId,
		draggingType,
		hotTarget,
	}

	return (
		<div className="mt-2 select-none px-1.5" style={{ textShadow: '0 1px 1px rgba(255 255 255 0.3)' }}>
			<DropSlot parentId={null} idx={0} {...sharedProps} />
			{layerTree.map((node, i) => {
				const layer = layers[node.id]
				if (!layer) { return null }
				return (
					<div key={node.id}>
						<NodeView
							node={node}
							parentId={null}
							idx={i}
							layer={layer}
							expanded={expanded}
							onToggleExpand={toggleExpand}
							layers={layers}
							inheritedHidden={false}
							inheritedLocked={false}
							{...sharedProps}
						/>
						<DropSlot parentId={null} idx={i + 1} {...sharedProps} />
					</div>
				)
			})}
		</div>
	)
}
